import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { AgentNotifications } from './AgentNotifications.js';

interface NavItem {
  to: string;
  // i18n key resolved with t() at render time (see nav.* in locales).
  labelKey: string;
  end?: boolean;
}

export function Layout() {
  const { user, logout, hasRole, canDistributeLeads } = useAuth();
  const { t } = useTranslation();
  // Mobile-only: the sidebar collapses into an off-canvas drawer (see styles.css).
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const mainNav: NavItem[] = [
    { to: '/', labelKey: 'nav.dashboard', end: true },
    { to: '/workspace', labelKey: 'nav.workspace' },
    { to: '/contacts', labelKey: 'nav.contacts' },
    { to: '/conversations', labelKey: 'nav.conversations' },
  ];

  const isPlatformAdmin = hasRole('platform_admin');
  const isReseller = hasRole('reseller_admin', 'reseller_user');

  const adminNav: NavItem[] = [
    { to: '/admin/sellers', labelKey: 'nav.sellers' },
    { to: '/admin/brands', labelKey: 'nav.brands' },
    { to: '/admin/sites', labelKey: 'nav.sites' },
    { to: '/admin/phone-numbers', labelKey: 'nav.phoneNumbers' },
  ];

  return (
    <div className="app-shell">
      <header className="mobile-topbar">
        <button
          type="button"
          className="mobile-topbar__toggle"
          aria-label={t(menuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
        <img
          className="mobile-topbar__logo"
          src="/tips-contact-logo.png"
          alt="Tips Contact"
          width={920}
          height={684}
        />
      </header>

      {menuOpen && <div className="sidebar-backdrop" onClick={closeMenu} />}

      <aside id="app-sidebar" className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <img
            className="sidebar__logo-img"
            src="/tips-contact-logo.png"
            alt="Tips Contact"
            width={920}
            height={684}
          />
        </div>

        {/* Clicks on any nav link bubble here and close the mobile drawer. */}
        <nav className="sidebar__nav" aria-label="Primary" onClick={closeMenu}>
          <div className="nav-group">
            <span className="nav-group__label">{t('nav.groups.workspace')}</span>
            {mainNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </div>

          {canDistributeLeads && (
            <div className="nav-group">
              <span className="nav-group__label">{t('nav.groups.sales')}</span>
              <NavLink
                to="/sales/lead-distribution"
                className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
              >
                {t('nav.leadDistribution')}
              </NavLink>
            </div>
          )}

          {isReseller && (
            <div className="nav-group">
              <span className="nav-group__label">{t('nav.groups.reseller')}</span>
              <NavLink
                to="/reseller"
                className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
              >
                {t('nav.portalHome')}
              </NavLink>
            </div>
          )}

          <div className="nav-group">
            <span className="nav-group__label">{t('nav.groups.settings')}</span>
            <NavLink
              to="/settings/api-keys"
              className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
            >
              {t('nav.apiKeys')}
            </NavLink>
          </div>

          {isPlatformAdmin && (
            <div className="nav-group">
              <span className="nav-group__label">{t('nav.groups.platformAdmin')}</span>
              {adminNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="sidebar__footer">
          {user && (
            <div className="user-chip">
              <div className="user-chip__avatar">{user.display_name.charAt(0).toUpperCase()}</div>
              <div className="user-chip__meta">
                <span className="user-chip__name">{user.display_name}</span>
                <span className="user-chip__role">{user.role.replace(/_/g, ' ')}</span>
              </div>
            </div>
          )}
          <LanguageSwitcher />
          <button type="button" className="btn btn--ghost btn--block" onClick={logout}>
            {t('common.signOut')}
          </button>
        </div>
      </aside>

      <main className="app-main">
        <AgentNotifications />
        <Outlet />
      </main>
    </div>
  );
}
